import createClient from "openapi-fetch";
//#region src/commands/auth/client.ts
const MASTRA_PLATFORM_API_URL = process.env.MASTRA_PLATFORM_API_URL || "https://platform.mastra.ai";
/**
* Derive the gateway URL from the platform URL when not explicitly set.
* If the platform points at staging, the gateway defaults to staging too.
*/
function deriveGatewayUrl() {
	if (process.env.MASTRA_GATEWAY_URL) return process.env.MASTRA_GATEWAY_URL;
	if (MASTRA_PLATFORM_API_URL.includes("staging")) return "https://gateway-api.staging.mastra.ai/v1";
	return "https://gateway-api.mastra.ai/v1";
}
deriveGatewayUrl();
/**
* Derive the studio URL from the platform URL when not explicitly set.
* If the platform points at staging, the studio defaults to staging too.
*/
function deriveStudioUrl() {
	if (process.env.MASTRA_STUDIO_URL) return process.env.MASTRA_STUDIO_URL;
	if (MASTRA_PLATFORM_API_URL.includes("staging")) return "https://studio.staging.mastra.ai";
	return "https://studio.mastra.ai";
}
const MASTRA_STUDIO_URL = deriveStudioUrl();
const SESSION_EXPIRED_MESSAGE = "Session expired. Run: mastra auth login";
/**
* Throw a standardized error for API failures.
* - 401: "Session expired" (authentication failed)
* - Other: Show the server's error detail or fall back to status code
*/
function throwApiError(message, status, detail) {
	if (status === 401) throw new Error(SESSION_EXPIRED_MESSAGE);
	if (detail) throw new Error(detail);
	throw new Error(`${message}: ${status}`);
}
/** Best-effort message from platform JSON error bodies (RFC 7807 `detail`, etc.). */
function extractApiErrorDetail(error) {
	if (!error || typeof error !== "object") return void 0;
	const o = error;
	let detail;
	if (typeof o.detail === "string" && o.detail.trim()) detail = o.detail;
	else if (typeof o.message === "string" && o.message.trim()) detail = o.message;
	else if (typeof o.error === "string" && o.error.trim()) detail = o.error;
	const fieldErrors = Array.isArray(o.errors) ? o.errors.map((e) => {
		if (!e || typeof e !== "object") return "";
		const fe = e;
		return [typeof fe.field === "string" ? fe.field : "", typeof fe.message === "string" ? fe.message : ""].filter(Boolean).join(" — ");
	}).filter(Boolean).join("; ") : "";
	if (detail && fieldErrors) return `${detail}: ${fieldErrors}`;
	return detail ?? (fieldErrors || void 0);
}
let _currentToken = null;
let _refreshInFlight = null;
/**
* Set the current token/orgId used by authenticated fetch.
* Call this after login or getToken().
*/
function setCurrentAuth(token, orgId) {
	_currentToken = token;
	if (orgId !== void 0);
}
/**
* A fetch wrapper that auto-refreshes the token on 401 and retries once.
* Used by both createApiClient (openapi-fetch) and raw fetch calls.
*/
async function authenticatedFetch(input, init) {
	const clonedRequest = input instanceof Request ? input.clone() : null;
	if (!_currentToken) {
		const authHeader = (input instanceof Request ? input.headers.get("Authorization") : null) ?? (init?.headers instanceof Headers ? init.headers.get("Authorization") : typeof init?.headers === "object" && init.headers && !Array.isArray(init.headers) ? init.headers["Authorization"] : null);
		if (authHeader?.startsWith("Bearer ")) _currentToken = authHeader.slice(7);
	}
	const response = await fetch(input, init);
	if (response.status !== 401 || !_currentToken) return response;
	if (!_refreshInFlight) _refreshInFlight = (async () => {
		try {
			const { tryRefreshToken, loadCredentials } = await import("./orgs-DoKnmO72.js").then((n) => n.c);
			const creds = await loadCredentials();
			if (!creds) throw new Error("No credentials");
			const newToken = await tryRefreshToken(creds);
			if (!newToken) throw new Error("Refresh failed");
			_currentToken = newToken;
			return newToken;
		} finally {
			_refreshInFlight = null;
		}
	})();
	let newToken;
	try {
		newToken = await _refreshInFlight;
	} catch {
		return response;
	}
	if (clonedRequest) {
		const retryHeaders = new Headers(clonedRequest.headers);
		retryHeaders.set("Authorization", `Bearer ${newToken}`);
		return fetch(new Request(clonedRequest, { headers: retryHeaders }));
	}
	const retryHeaders = new Headers(init?.headers);
	retryHeaders.set("Authorization", `Bearer ${newToken}`);
	return fetch(input, {
		...init,
		headers: retryHeaders
	});
}
/**
* Create a typed API client with Bearer token + org ID headers.
* Uses authenticatedFetch for automatic 401 retry.
*/
function createApiClient(token, orgId) {
	setCurrentAuth(token, orgId);
	const headers = { Authorization: `Bearer ${token}` };
	if (orgId) headers["x-organization-id"] = orgId;
	return createClient({
		baseUrl: MASTRA_PLATFORM_API_URL,
		headers,
		fetch: authenticatedFetch
	});
}
/**
* Build auth headers for raw fetch calls (zip upload, SSE streaming, tokens).
*/
function authHeaders(token, orgId) {
	const headers = { Authorization: `Bearer ${token}` };
	if (orgId) headers["x-organization-id"] = orgId;
	return headers;
}
/**
* Make an authenticated fetch call that auto-refreshes on 401.
* Use this instead of raw fetch() for platform API calls.
*/
function platformFetch(input, init) {
	return authenticatedFetch(input, init);
}
//#endregion
export { extractApiErrorDetail as a, throwApiError as c, createApiClient as i, MASTRA_STUDIO_URL as n, platformFetch as o, authHeaders as r, setCurrentAuth as s, MASTRA_PLATFORM_API_URL as t };

//# sourceMappingURL=client-RyOogiHS.js.map